/**
 * Calculates interest amount. 
 * NOTE: All params are required & default to 0.
 * @param {Number} total_mortgage - Total mortgage amount.
 * @param {Number} interest_rate - Interest rate.
 * @param {Number} pay_freq - Payment frequency.
 * @returns {Number} Interest Amount.
 */
export function getInterest (total_mortgage = 0, interest_rate = 0, pay_freq = 0) {
  total_mortgage = parseFloat(total_mortgage);
  interest_rate = parseFloat(interest_rate);
  pay_freq = parseFloat(pay_freq);

  let rate = interest_rate / 100, monthlyInterest = (rate / pay_freq) * total_mortgage;
  
  if (monthlyInterest < 0) return 0;
  return parseFloat(monthlyInterest);
};

/**
 * Calculates monthly payment amount based on frequency.
 * NOTE: All params are required & default to 0.
 * @param {Number} monthly_mortgage - Monthly mortgage amount.
 * @param {Number} frequency - Payment frequency.
 * @returns {Number} Monthly payment amount.
 */
export function monthlyToFrequency (monthly_mortgage = 0, frequency = 0) {
  let freq_based_mortgage = 0;

  switch (frequency) {
    case '12':
      freq_based_mortgage = monthly_mortgage;
      break;
    case '24':
      freq_based_mortgage = monthly_mortgage / 2;
      break;
    case '26':
      freq_based_mortgage = (12 * monthly_mortgage) / 26;
      break;
    case '52':
      freq_based_mortgage = (12 * monthly_mortgage) / 52;
      break;
    case '28':
      freq_based_mortgage = (12 * monthly_mortgage) / 26;
      freq_based_mortgage = (27 * freq_based_mortgage) / 26;
      break;
    case '53':
      freq_based_mortgage = (12 * monthly_mortgage) / 52;
      freq_based_mortgage = (53 * freq_based_mortgage) / 52;
      break;
    default:
      freq_based_mortgage = monthly_mortgage;
      break;
  }

  return freq_based_mortgage;
};

/**
 * Calculates monthly payment amount based on period & interest rate.
 * NOTE: All params are required & default to 0.
 * @param {Number} total_mortgage - Total mortgage amount.
 * @param {Number} interestRate - Interest rate.
 * @param {Number} period - Payment period.
 * @returns {Number} Monthly payment amount.
 */
export function monthlyMortgage(total_mortgage = 0, interestRate = 0, period = 0) {
  total_mortgage = parseFloat(total_mortgage);
  interestRate = parseFloat(interestRate);
  period = parseFloat(period);

  interestRate = interestRate <= 0 ? 0.000001 : interestRate;
  
  let rate = interestRate / 12 / 100,
      periodMonthly = period * 12,
      powered = Math.pow(1 + rate, periodMonthly),
      numerator = rate * powered,
      denominator = powered - 1,
      fraction = numerator / denominator,
      monthlyMortgage = total_mortgage * fraction;
  
  if (monthlyMortgage < 0) return 0;
  
  return parseFloat(monthlyMortgage);
}

/**
 * Calculate the CMHC Insurance amount based on total mortgage & down payment percentage.
 * NOTE: All params are required & default to 0.
 * @param {Number} total_mortgage - Total mortgage amount. 
 * @param {Number} dpPercentage - Down payment percentage.
 * @returns {Number} CMHC Insurance amount.
 */
export function cmhcInsurance(total_mortgage = 0, dpPercentage = 0) {
  let preCmhc = parseFloat(total_mortgage).toFixed(3), cmhc;
  
  if (parseFloat(dpPercentage) >= 20) cmhc = 0;
  else {
    if (dpPercentage < 10) cmhc = (4 / 100) * preCmhc;
    if (dpPercentage >= 10 && dpPercentage < 15) cmhc = (3.1 / 100) * preCmhc;
    if (dpPercentage >= 15 && dpPercentage < 20) cmhc = (2.8 / 100) * preCmhc;
  }

  return cmhc;
}

/**
 * Calculates the present value of the loan based on the interest rate, number of payments, and payment amount.
 * @param {Number} rate - Interest rate.
 * @param {Number} nper
 * @param {Number} pmt - Payment amount.
 * @param {Number} fv - Future value.
 * @returns {Number} Present value.
 */
export function presentValue(rate = 0, nper = 0, pmt = 0, fv = 0) {
  rate = parseFloat(rate);
  nper = parseFloat(nper);
  pmt = parseFloat(pmt);
  fv = parseFloat(fv);

  rate = rate / 100 / 12;
  nper = nper * 12;
  pmt = -pmt;
  
  let pv_value, x, y;

  if (rate === 0) pv_value = -(fv + pmt * nper);
  else {
    x = Math.pow(1 + rate, -nper); 
    y = Math.pow(1 + rate, nper);
    pv_value = -(x * (fv * rate - pmt + y * pmt)) / rate;
  }
  
  pv_value = pv_value.toFixed(0);
  
  return parseFloat(pv_value);
}